import React, { useMemo, useCallback, ReactElement } from 'react';
import Table from '../../../../../../components/table/table.component';
import TableBody from '../../../../../../components/table-body/table-body.component';
import TableRow from '../../../../../../components/table-row/table-row.component';
import TableCell from '../../../../../../components/table-cell/table-cell.component';
import Button from '../../../../../../components/button/button.component';
import FilteredLink from '../../../../../../components/filtered-link/filtered-link.component';
import Hint from '../../../../../../components/hint/hint.component';
import Chip from '../../../../../../components/chip/chip.component';
import CheckAccess from '../../../../../../components/check-access/check-access.component';
import FormFieldSelect from '../../../../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect
  from '../../../../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldCheckbox from '../../../../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../../../../app/common/single-page-tab/single-page-tab.component';
import PushNotificationService from '../../../../../../services/push-notification-service';
import StructureService from '../../../../../../services/structure-service';
import FormatService from '../../../../../../services/format-service';
import {
  SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS, SETTING_NAME_HOMEPAGE, SETTING_NAME_SEARCH_RESULT_GROUPS,
  SETTING_NAME_SHOW_NOTIFICATIONS,
} from '../../../../../../constants/entity/setting-names';
import {
  ACTIVITY_LIST_VIEW, ASSET_LIST_VIEW, CONTACT_VIEW, DEAL_LIST_VIEW, PORTFOLIO_LIST_VIEW, REPORT_LIST_VIEW,
  SEARCH_RESULT_VIEW,
} from '../../../../../../constants/entity/permissions';
import {
  ROUTE_NAME_ACTIVITY_LIST, ROUTE_NAME_ASSET_LIST, ROUTE_NAME_CONTACT_LIST, ROUTE_NAME_DEAL_LIST,
  ROUTE_NAME_REPORT_LIST,
} from '../../../../../../constants/entity/route-names';
import { SETTING_TYPE_USER_SETTINGS } from '../../../../../../constants/entity/setting-types';
import { SEARCH_RESULT_GROUP_LIST } from '../../../../../../constants/system/search-result-groups';

const SettingsUserPreferences: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ROUTE_PERMISSION_MAP: Record<string, string> = {
    [ROUTE_NAME_ASSET_LIST]: ASSET_LIST_VIEW,
    [ROUTE_NAME_DEAL_LIST]: DEAL_LIST_VIEW,
    [ROUTE_NAME_ACTIVITY_LIST]: ACTIVITY_LIST_VIEW,
    [ROUTE_NAME_CONTACT_LIST]: CONTACT_VIEW,
    [ROUTE_NAME_REPORT_LIST]: REPORT_LIST_VIEW,
  };

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    routeList,
    portfolioList,
    userData,
    selectedDefaultPortfolios,
    selectedSearchResultGroups,
    onCancelForm,
    onSubmitForm,
    onDefaultPortfolioValuesChange,
    onSearchResultGroupValuesChange,
  } = props;

  const formPrefix: string = 'userSettings';
  const userSettingsData: Record<string, SettingValue> | null = userData && userData.settings
  && userData.settings.length ? StructureService.getItemMap(
    userData.settings.filter((userSetting: Setting): boolean => userSetting.type === SETTING_TYPE_USER_SETTINGS),
    'name',
    'value',
  ) : null;
  const userSettingsHomepageRoute: Route = userSettingsData && userSettingsData[SETTING_NAME_HOMEPAGE]
  && routeList && routeList.length
    ? routeList.find((routeItem: Route): boolean => routeItem.name === userSettingsData[SETTING_NAME_HOMEPAGE]) : null;
  const userSettingsDefaultPortfolioFilterIds: number[] = (userSettingsData
    && userSettingsData[SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS]) || [];
  const userSettingsShowNotifications: boolean = (userSettingsData && userSettingsData[SETTING_NAME_SHOW_NOTIFICATIONS])
    || false;
  const userSettingsSearchResultGroups: string[] = ((userSettingsData
    && userSettingsData[SETTING_NAME_SEARCH_RESULT_GROUPS]) || []);
  const permissionRouteList: PermissionRoute[] = routeList && routeList.length
    ? routeList.map((routeItem: Route): PermissionRoute => ({
      ...routeItem,
      permission: (routeItem.name && ROUTE_PERMISSION_MAP[routeItem.name]) || null,
    })) : [];
  const webPushNotificationsEnabled: boolean = PushNotificationService.checkIfWebPushNotificationsEnabled();
  const webPushNotificationsAllowed: boolean = PushNotificationService.checkIfWebPushNotificationsAllowed();

  const portfolioListViewPermissions: string[] = useMemo((): string[] => [PORTFOLIO_LIST_VIEW], []);
  const searchResultListViewPermissions: string[] = useMemo((): string[] => [SEARCH_RESULT_VIEW], []);

  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    portfolioList && portfolioList.length ? portfolioList
      .map((portfolioItem: Portfolio): number => portfolioItem.group && portfolioItem.group.id)
      .filter((portfolioGroupId: number, portfolioGroupIndex: number, portfolioItemList: number[]): boolean => (
        portfolioItemList.indexOf(portfolioGroupId) === portfolioGroupIndex
      )).length > 1 : false
  ), [portfolioList]);

  const menuItemRouteList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withPermissions: true,
    optionItemValueKey: 'name',
    optionList: permissionRouteList,
  }), [permissionRouteList]);

  const allowPushNotifications: MouseEventHandlerFunction = useCallback(async (): Promise<void> => {
    await PushNotificationService.allowWebPushNotifications();
  }, []);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      isEmpty={!userSettingsData}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Homepage
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldSelect
                    isBordered
                    isDisabled={routeList && !routeList.length}
                    name={`${formPrefix}[${SETTING_NAME_HOMEPAGE}].value`}
                    optionsTotal={routeList && routeList.length}
                  >
                    {menuItemRouteList}
                  </FormFieldSelect>
                ) : (
                  userSettingsHomepageRoute.route && userSettingsHomepageRoute.title && (
                    <FilteredLink isExternal link={userSettingsHomepageRoute.route}>
                      {userSettingsHomepageRoute.title}
                    </FilteredLink>
                  )
                )
              }
            </TableCell>
          </TableRow>
          {
            portfolioList && !!portfolioList.length && (
              <CheckAccess permissions={portfolioListViewPermissions}>
                <TableRow>
                  <TableCell>
                    Default Portfolio Filter
                  </TableCell>
                  <TableCell>
                    {
                      (isEditMode && (
                        <FormFieldMultiSelect
                          isBordered
                          name={`${formPrefix}[${SETTING_NAME_DEFAULT_PORTFOLIO_FILTER_IDS}].value`}
                          optionList={portfolioList}
                          onValuesChange={(portfolioValues: Portfolio[]): void => {
                            onDefaultPortfolioValuesChange(portfolioValues);
                          }}
                        >
                          {
                            MenuItemList({
                              isGrouped: isMultiplePortfolioGroupView,
                              groupKey: 'group',
                              groupItemLabelKey: 'name',
                              optionItemLabelKey: 'name',
                              optionList: portfolioList,
                              selectedItemList: selectedDefaultPortfolios,
                            })
                          }
                        </FormFieldMultiSelect>
                      )) || (
                        userSettingsDefaultPortfolioFilterIds.length
                          ? userSettingsDefaultPortfolioFilterIds.map((portfolioId: number): ReactElement | null => {
                            const portfolio: Portfolio | undefined = portfolioList.find((
                              portfolioItem: Portfolio,
                            ): boolean => (
                              portfolioItem.id === portfolioId
                            ));

                            if (!portfolio) {
                              return null;
                            }

                            return (
                              <Chip
                                isDisabled
                                key={portfolio.id}
                                label={portfolio.name}
                              />
                            );
                          }).filter((portfolioElement: ReactElement | null): boolean => !!portfolioElement)
                          : 'None'
                      )
                    }
                  </TableCell>
                </TableRow>
              </CheckAccess>
            )
          }
          <CheckAccess permissions={searchResultListViewPermissions}>
            <TableRow>
              <TableCell>
                Search Result Groups Visible
              </TableCell>
              <TableCell>
                {
                  (isEditMode && (
                    <FormFieldMultiSelect
                      isBordered
                      name={`${formPrefix}[${SETTING_NAME_SEARCH_RESULT_GROUPS}].value`}
                      optionList={SEARCH_RESULT_GROUP_LIST}
                      onValuesChange={(searchResultGroupValues: SearchResultGroup[]): void => {
                        onSearchResultGroupValuesChange(searchResultGroupValues);
                      }}
                    >
                      {
                        MenuItemList({
                          optionList: SEARCH_RESULT_GROUP_LIST,
                          selectedItemList: selectedSearchResultGroups,
                        })
                      }
                    </FormFieldMultiSelect>
                  )) || (
                    userSettingsSearchResultGroups.length
                      ? userSettingsSearchResultGroups.map((
                        searchResultGroupName: string,
                      ): ReactElement | null => {
                        const searchResultGroup: SearchResultGroup | undefined = SEARCH_RESULT_GROUP_LIST.find((
                          searchResultGroup: SearchResultGroup,
                        ): boolean => (
                          searchResultGroupName === searchResultGroup.name
                        ));

                        if (!searchResultGroup) {
                          return null;
                        }

                        return (
                          <Chip
                            isDisabled
                            key={searchResultGroup.id}
                            label={searchResultGroup.title}
                          />
                        );
                      }).filter((searchResultGroupElement: ReactElement | null): boolean => (
                        !!searchResultGroupElement
                      ))
                      : 'None'
                  )
                }
              </TableCell>
            </TableRow>
          </CheckAccess>
          <TableRow>
            <TableCell>
              Show Notification Pop-ups
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldCheckbox name={`${formPrefix}[${SETTING_NAME_SHOW_NOTIFICATIONS}].value`} />
                ) : FormatService.formatBooleanValue(userSettingsShowNotifications)
              }
            </TableCell>
          </TableRow>
          {
            webPushNotificationsEnabled && (
              <TableRow>
                <TableCell>
                  Web Push Notifications
                  <Hint title="Subscribe to In-App notifications with your browser" />
                </TableCell>
                <TableCell>
                  {
                    webPushNotificationsAllowed ? 'Allowed' : (
                      (isEditMode && (
                        <Button
                          isText
                          isActive
                          onClick={allowPushNotifications}
                        >
                          Allow
                        </Button>
                      )) || 'Disabled'
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default SettingsUserPreferences;
